import { Music, Play, Pause } from "lucide-react";
import { useState, useRef } from "react";
import { Card } from "@/components/ui/card";
import neilAiSong from "@/assets/neil-ai-song.mp3";
import heKeepWinning from "@/assets/he-keep-winning.mp3";
import noMoreBurnout from "@/assets/music/no-more-burnout.mp3.asset.json";

const AIMusicSection = () => {
  const songs = [
    {
      title: "No More Burnout",
      description: "Neil AI anthem — automate, elevate, dominate",
      src: noMoreBurnout.url
    },
    {
      title: "Neil AI Theme Song",
      description: "The official sound of the future",
      src: neilAiSong
    },
    {
      title: "He Keep Winning",
      description: "Celebrating AI success",
      src: heKeepWinning
    }
  ];

  const [currentSongIndex, setCurrentSongIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  // No autoplay — music only plays when the user presses play


  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleSongChange = (index: number) => {
    setCurrentSongIndex(index);
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.load();
      // resume playback for the newly selected song
      setTimeout(() => {
        audioRef.current?.play().then(() => setIsPlaying(true)).catch(() => {});
      }, 100);
    }
  };

  return (
    <section className="mb-16">
      <div className="text-center mb-8">
        <h2 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">
          AI Music
        </h2>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Experience the sound of innovation with our AI-generated music collection
        </p>
      </div>

      <Card className="glass-effect border-[hsl(var(--line))] p-8 max-w-2xl mx-auto">
        <div className="flex items-center gap-6 mb-6">
          <button
            onClick={togglePlay}
            className="w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center hover:bg-primary/90 transition-all hover:scale-105"
            aria-label={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? <Pause className="w-8 h-8" /> : <Play className="w-8 h-8 ml-1" />}
          </button>

          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <Music className="w-5 h-5 text-primary" />
              <h3 className="text-xl font-semibold text-foreground">{songs[currentSongIndex].title}</h3>
            </div>
            <p className="text-sm text-muted-foreground">{songs[currentSongIndex].description}</p>
          </div>
        </div>

        <audio
          ref={audioRef}
          src={songs[currentSongIndex].src}
          onEnded={() => setIsPlaying(false)}
          className="w-full mb-4"
          controls
        />

        <div className="flex gap-2">
          {songs.map((song, index) => (
            <button
              key={index}
              onClick={() => handleSongChange(index)}
              className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-all ${
                currentSongIndex === index
                  ? "bg-primary text-primary-foreground"
                  : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
              }`}
            >
              {song.title}
            </button>
          ))}
        </div>
      </Card>
    </section>
  );
};

export default AIMusicSection;
